import React, { memo, useCallback, useState } from 'react'

import {
  Icon,
  Button,
  RotatingSpinner,
  Trash,
  ShareOutline,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useDccBridgeAccess from 'Pods/ThreeDStudio/Hooks/useDccBridgeAccess'
import useDeleteModelSession from 'Pods/ThreeDStudio/Hooks/useDeleteModelSession'
import useExportAccess from 'Pods/ThreeDStudio/Hooks/useExportAccess'
import useOpenedModelReadOnly from 'Pods/ThreeDStudio/Hooks/useOpenedModelReadOnly'
import useStudioAccessTierPending from 'Pods/ThreeDStudio/Hooks/useStudioAccessTierPending'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { STUDIO_EVENT, trackStudioEvent } from 'Pods/ThreeDStudio/Services/studioTracker'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import PaidFeatureBadge from 'Pods/ThreeDStudio/Shared/PaidFeatureBadge/PaidFeatureBadge'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import DccBridgeButton from './Components/DccBridgeButton/DccBridgeButton'
import ExportButton from './Components/ExportButton/ExportButton'
import ExportFormatButton from './Components/ExportFormatButton/ExportFormatButton'
import ModelTitle from './Components/ModelTitle/ModelTitle'
import SendToButton from './Components/SendToButton/SendToButton'
import SendToModal from './Components/SendToModal/SendToModal'

const deleteIcon = <Icon svgComponent={Trash} color="darker" size={iconSize} notSpaced />
const shareIcon = <Icon svgComponent={ShareOutline} color="darker" size={iconSize} notSpaced />
const loadingShareIcon = <Icon svgComponent={RotatingSpinner} color="darker" size={iconSize} notSpaced />

let ShareModal
let ThreeDStudioShareContent

function FooterActions() {
  const t = useTranslation()
  const generatorSessionId = useThreeDStudioStore(state => state.generation?.generatorSessionId)
  const modelTitle = useThreeDStudioStore(
    state => state.ui?.openedModel?.title ?? state.ui?.openedModel?.prompt,
  )
  const runId = useThreeDStudioStore(state => state.ui?.openedModel?.id)
  const isReadOnly = useOpenedModelReadOnly()
  // FooterActions is client-only (it mounts only once a model is opened), so gating on
  // the login-pending window is safe here - there is no SSR/hydration boundary. While
  // the tier is still loading we treat a logged-in user as entitled so the paid crown
  // badge (and its "For Subscribers Only" announcement) never flashes for subscribed
  // or credit users who open a model before quota hydration completes. The buttons
  // themselves read their own access hook and open the paid-feature modal when locked.
  const isTierPending = useStudioAccessTierPending()
  const canUseDccBridge = useDccBridgeAccess() || isTierPending
  const canExport = useExportAccess() || isTierPending

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isShareModalLoading, setIsShareModalLoading] = useState(false)
  const [isShareModalLoaded, setIsShareModalLoaded] = useState(() => !!ShareModal)

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const { deleteModel, isLoading: isDeleting } = useDeleteModelSession({ onSuccess: handleCloseDeleteModal })

  const handleOpenDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    deleteModel(generatorSessionId)
  }, [deleteModel, generatorSessionId])

  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false)
  }, [])

  const importShareModal = useCallback(() => {
    setIsShareModalLoading(true)
    Promise.all([
      import('Pods/LazyLoaded/ShareModal/ShareModal'),
      import('Pods/ThreeDStudio/Shared/ShareContent/ThreeDStudioShareContent'),
    ])
      .then(([shareModalModule, shareContentModule]) => {
        ShareModal = shareModalModule.default
        ThreeDStudioShareContent = shareContentModule.default
        setIsShareModalLoaded(true)
      })
      .catch((error) => {
        console.error('Failed to load ShareModal:', error)
      })
      .finally(() => {
        setIsShareModalLoading(false)
      })
  }, [])

  const handleShareClick = useCallback(() => {
    if (!runId) return
    trackStudioEvent(STUDIO_EVENT.SHARE_CLICK, { run_id: runId })
    setIsShareModalOpen(true)
    if (!ShareModal && !isShareModalLoaded && !isShareModalLoading) {
      importShareModal()
    }
  }, [runId, isShareModalLoaded, isShareModalLoading, importShareModal])

  const isShareDisabled = !runId || isShareModalLoading

  // Owner-only actions are hidden when viewing someone else's model; the
  // backend would reject delete/title/settings mutations anyway.
  if (isReadOnly) {
    return (
      <div className="footer-actions">
        <ModelTitle />
      </div>
    )
  }

  return (
    <div className="footer-actions">
      <ModelTitle />
      <div className="footer-actions__actions">
        <Button
          icon={deleteIcon}
          type="secondary"
          size="small"
          variant="only-icon"
          aria-label="Delete"
          customClass="footer-actions__delete"
          onClick={handleOpenDeleteModal}
          disabled={isDeleting}
        />
        <Button
          icon={isShareModalLoading ? loadingShareIcon : shareIcon}
          type="secondary"
          size="small"
          variant="only-icon"
          aria-label="Share"
          onClick={handleShareClick}
          disabled={isShareDisabled}
        />
        <div className="dcc-bridge-cluster">
          <SendToButton />
          <DccBridgeButton />
          {!canUseDccBridge && (
            <PaidFeatureBadge className="dcc-bridge-cluster__badge" label={t('shared.subscribersOnly.title')} />
          )}
        </div>

        <div className="export-cluster">
          <ExportButton />
          <ExportFormatButton />
          {!canExport && (
            <PaidFeatureBadge className="export-cluster__badge" label={t('shared.subscribersOnly.title')} />
          )}
        </div>
      </div>
      <SendToModal />
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="model"
          itemName={modelTitle}
          isSubmitDisabled={isDeleting}
          onSubmit={handleConfirmDelete}
          onCancel={handleCloseDeleteModal}
        />
      )}
      {isShareModalOpen && isShareModalLoaded && ShareModal && ThreeDStudioShareContent && (
        <ShareModal
          closeModal={handleCloseShareModal}
          content={<ThreeDStudioShareContent />}
        />
      )}
    </div>
  )
}

export default memo(FooterActions)
